import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link } from "react-router-dom"
import { CreateUserAction, UpdateUserAction } from "src/components/superadmin/users/SuperAdminUserActions"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { UserFragment, User_Status_Enum, User_Type_Enum, Users_Bool_Exp } from "src/state/gql/_types"
import { capitalizeWord } from "src/utils/string"
import { getMappedUserRoleAdmin } from "src/utils/users"
import { formatDate } from "src/utils/dates"

export function SuperAdminUsers() {

    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.users)
    const { 
        subscribeToItems, 
        unsubscribeFromItems, 
        getMoreItems, 
        resetModule, 
        setMeta,
        selectItem
    } = useActions().users
    const { view } = useAppState(state => state.ui)

    const {
        filters,
        filterParams
    } = usePageFilters<Users_Bool_Exp['_and'], UserFragment>({
        baseFilters: [{
            tenant_id: { _is_null: true },
            type: { _neq: User_Type_Enum.Talent }
        }],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: User_Status_Enum.Active } }
                ]
            }
        ],
        searchFilterFields: ['email','first_name','last_name','user_number'],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'active',
        clickFilterValues: {
            active: { label: 'Active', filter: [{ status: { _eq: User_Status_Enum.Active } }] },
            invited: { label: 'Invited', filter: [{ status: { _eq: User_Status_Enum.Invited } }] },
            inactive: { label: 'Inactive', filter: [{ status: { _eq: User_Status_Enum.Inactive } }] },
            all: { label: 'All', filter: [] }
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<UserFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'ID'
        }),
        columnHelper.accessor('user_number', {
            header: '#'
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: row => capitalizeWord(row.getValue())
        }),
        columnHelper.accessor('type', {
            header: 'Department',
            cell: row => capitalizeWord(row.getValue())
        }),
        columnHelper.accessor('role', {
            header: 'Role',
            cell: row => getMappedUserRoleAdmin(row.getValue())
        }),
        columnHelper.accessor('email', {
            header: 'Email'
        }),
        columnHelper.accessor('first_name', {
            header: 'First Name'
        }),
        columnHelper.accessor('last_name', {
            header: 'Last_Name'
        }),
        columnHelper.accessor('created_at', {
            header: 'Created',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
        columnHelper.accessor('updated_at', {
            header: 'Updated',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<UserFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        //navigation(`/users/${id}`)
        selectItem(id)
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/users/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.email}</Text>
                                        <Badge variant="outline">{i.status}</Badge>
                                    </Group>
                                    <Text size="sm">{i.email}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<UserFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Users`}
                    renderAction={
                        <Group>
                            <UpdateUserAction />
                            <CreateUserAction/>
                        </Group>
                    }
                />
            }
        >
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Users' />)}
        </PageLayout>
    )

}